// Pure helpers for @mention / $cashtag / URL parsing. No React, no DOM.
// Handle rules (SOC contract): 3-20 chars, [a-z0-9_], starts with a letter.

const MENTION_RE = /(^|[^A-Za-z0-9_@/.])@([A-Za-z][A-Za-z0-9_]{2,19})(?![A-Za-z0-9_@])/g;
const CASHTAG_RE = /(^|[^A-Za-z0-9_$])\$([A-Za-z][A-Za-z0-9]{0,5}(?:\.[A-Za-z])?)(?![A-Za-z0-9_$])/g;
const URL_RE = /https?:\/\/[^\s<>"'`]+/gi;

// Group mentions that are not people: rendered as a plain chip, no hover card.
export const GROUP_MENTIONS = ['here', 'everyone', 'room', 'all'];

const TRAILING = /[.,;:!?)\]}'"]+$/;

/** Is this a plausible http(s) URL? Anything else (javascript:, data:) is never linked. */
export function safeUrl(raw) {
  try {
    const u = new URL(raw);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.href : null;
  } catch { return null; }
}

/**
 * Split text into ordered segments:
 *   {type:'text', value} | {type:'mention', value:'handle'} | {type:'group', value:'here'}
 *   | {type:'cashtag', value:'AAPL'} | {type:'url', value:'https://..'}
 */
export function parseSegments(text) {
  const src = typeof text === 'string' ? text : '';
  const hits = [];

  let m;
  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(src))) {
    let raw = m[0];
    const trimmed = raw.replace(TRAILING, '');
    // keep a balanced closing paren, e.g. wikipedia links
    raw = trimmed;
    if (safeUrl(raw)) hits.push({ start: m.index, end: m.index + raw.length, type: 'url', value: raw });
  }
  const inUrl = (i) => hits.some((h) => h.type === 'url' && i >= h.start && i < h.end);

  MENTION_RE.lastIndex = 0;
  while ((m = MENTION_RE.exec(src))) {
    const start = m.index + m[1].length;
    if (inUrl(start)) continue;
    const handle = m[2].toLowerCase();
    hits.push({ start, end: start + 1 + m[2].length, type: GROUP_MENTIONS.includes(handle) ? 'group' : 'mention', value: handle });
  }
  CASHTAG_RE.lastIndex = 0;
  while ((m = CASHTAG_RE.exec(src))) {
    const start = m.index + m[1].length;
    if (inUrl(start)) continue;
    hits.push({ start, end: start + 1 + m[2].length, type: 'cashtag', value: m[2].toUpperCase() });
  }

  hits.sort((a, b) => a.start - b.start);
  const out = [];
  let pos = 0;
  hits.forEach((h) => {
    if (h.start < pos) return; // overlapped by an earlier hit
    if (h.start > pos) out.push({ type: 'text', value: src.slice(pos, h.start) });
    out.push({ type: h.type, value: h.value, raw: src.slice(h.start, h.end) });
    pos = h.end;
  });
  if (pos < src.length) out.push({ type: 'text', value: src.slice(pos) });
  return out;
}

/** Unique person handles mentioned in the text (lowercase). */
export function extractMentions(text) {
  return [...new Set(parseSegments(text).filter((s) => s.type === 'mention').map((s) => s.value))];
}

/** The @query being typed at `caret`, or null. start = index of the '@'. */
export function getMentionQuery(text, caret) {
  const upto = (text || '').slice(0, caret == null ? (text || '').length : caret);
  const m = /(^|[^A-Za-z0-9_@/.])@([A-Za-z0-9_]{0,20})$/.exec(upto);
  if (!m) return null;
  return { start: upto.length - m[2].length - 1, query: m[2].toLowerCase() };
}

/** Replace the in-progress @query with `@handle ` and report the new caret. */
export function applyMention(text, caret, handle) {
  const q = getMentionQuery(text, caret);
  if (!q) return { text, caret: caret == null ? text.length : caret };
  const c = caret == null ? text.length : caret;
  const insert = `@${handle} `;
  const next = text.slice(0, q.start) + insert + text.slice(c);
  return { text: next, caret: q.start + insert.length };
}
